import { db } from '../firebase';
import { doc, getDoc, increment, setDoc, updateDoc } from 'firebase/firestore';
import { PublicProfileService } from './PublicProfileService';

const APPLIED_KEY = 'referral_applied';

export const ReferralService = {
  getReferralLink(code) {
    return `${window.location.origin}/register?ref=${code}`;
  },

  /**
   * Applies a referral code once, immediately after a new account is
   * created — must be called while the new user is still the active auth
   * session (i.e. before AuthContext.register() signs them out to wait for
   * email verification), since writing `referredBy` onto the new user's own
   * doc requires request.auth.uid === uid.
   *
   * Grants a welcome streak freeze to the new user and credits the referrer
   * with +1 referral and a streak freeze.
   *
   * LIMITATION: there's no Cloud Function here to do this atomically or
   * with server-side validation (e.g. confirming the account is genuinely
   * new, rate-limiting). The Firestore rules for this restrict the write to
   * only the referralCount/streakFreezes/updatedAt fields to limit blast
   * radius, but a determined client could still spam a referrer's counters.
   * Acceptable for a reward this small (a streak freeze); replace with a
   * callable Cloud Function before this reward has real monetary value.
   */
  async applyCode(code, newUserUid) {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized || !db || !newUserUid) return { ok: false };
    if (localStorage.getItem(APPLIED_KEY)) return { ok: false, reason: 'already_applied' };

    const referrer = await PublicProfileService.findByInviteCode(normalized);
    if (!referrer || referrer.uid === newUserUid) return { ok: false, reason: 'invalid' };

    try {
      await setDoc(doc(db, 'users', newUserUid), {
        referredBy: referrer.uid,
        streakFreezes: increment(1),
      }, { merge: true });
      await updateDoc(doc(db, 'users', referrer.uid), {
        referralCount: increment(1),
        streakFreezes: increment(1),
        updatedAt: new Date().toISOString(),
      });
      localStorage.setItem(APPLIED_KEY, '1');
      return { ok: true, referrerName: referrer.displayName };
    } catch (e) {
      console.warn('ReferralService: applyCode failed', e);
      return { ok: false, reason: 'error' };
    }
  },

  async getStats(uid) {
    if (!db || !uid) return { referralCount: 0 };
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      return { referralCount: Number(snap.data()?.referralCount) || 0 };
    } catch {
      return { referralCount: 0 };
    }
  },
};
